/**
 * Impersonation API Client
 *
 * Handles client user impersonation for support purposes.
 */
import apiClient from './index'

export interface ImpersonationSession {
  id: string
  admin_user_id: string
  admin_email: string
  admin_name: string
  admin_role: string
  client_user_id: string
  client_email: string
  client_name: string
  client_company_id: string
  client_company_name: string
  reason: string
  started_at: string | null
  ended_at: string | null
  expires_at: string | null
  is_active: boolean
  actions_count: number
  last_action_at: string | null
}

export interface ClientUser {
  id: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  job_title?: string
  role: 'viewer' | 'collaborator' | 'admin'
  is_primary_contact: boolean
  is_active: boolean
  last_login?: string
}

export interface ClientUserPermissions {
  can_view_projects: boolean
  can_view_invoices: boolean
  can_view_proposals: boolean
  can_approve_proposals: boolean
  can_view_files: boolean
  can_upload_files: boolean
  can_add_comments: boolean
  can_view_time_entries: boolean
}

export interface StartImpersonationResponse {
  success: boolean
  message: string
  session: ImpersonationSession
  client_user: {
    id: string
    email: string
    name: string
    company_id: string
    company_name: string
    role: string
    permissions: ClientUserPermissions
  }
}

export interface ImpersonationStatus {
  is_impersonating: boolean
  session: ImpersonationSession | null
}

export interface AuditLog {
  id: string
  action_type: string
  action_description: string
  entity_type?: string
  entity_id?: string
  request_method: string
  request_path: string
  created_at: string
}

export interface CreateClientUserData {
  email: string
  first_name?: string
  last_name?: string
  job_title?: string
  role?: 'viewer' | 'collaborator' | 'admin'
  is_primary_contact?: boolean
  permissions?: Partial<ClientUserPermissions>
}

/**
 * Start impersonating a client user.
 */
export async function startImpersonation(
  clientUserId: string,
  reason?: string,
  durationHours?: number
): Promise<StartImpersonationResponse> {
  const response = await apiClient.post<StartImpersonationResponse>('/impersonate/start', {
    client_user_id: clientUserId,
    reason,
    duration_hours: durationHours,
  })
  return response.data
}

/**
 * Stop the current impersonation session.
 */
export async function stopImpersonation(): Promise<{ success: boolean; message: string; session: ImpersonationSession }> {
  const response = await apiClient.post<{ success: boolean; message: string; session: ImpersonationSession }>(
    '/impersonate/stop'
  )
  return response.data
}

/**
 * Get current impersonation status.
 */
export async function getImpersonationStatus(): Promise<ImpersonationStatus> {
  const response = await apiClient.get<ImpersonationStatus>('/impersonate/status')
  return response.data
}

/**
 * List impersonation sessions for audit.
 */
export async function listImpersonationSessions(params?: {
  client_user_id?: string
  company_id?: string
  limit?: number
}): Promise<{ data: ImpersonationSession[]; count: number }> {
  const response = await apiClient.get<{ data: ImpersonationSession[]; count: number }>('/impersonate/sessions', {
    params,
  })
  return response.data
}

/**
 * Get audit logs for an impersonation session.
 */
export async function getSessionAuditLogs(
  sessionId: string
): Promise<{ session: ImpersonationSession; logs: AuditLog[] }> {
  const response = await apiClient.get<{ session: ImpersonationSession; logs: AuditLog[] }>(
    `/impersonate/sessions/${sessionId}/logs`
  )
  return response.data
}

/**
 * List client portal users for a company.
 */
export async function listClientUsers(companyId: string): Promise<{ data: ClientUser[]; count: number }> {
  const response = await apiClient.get<{ data: ClientUser[]; count: number }>(`/clients/${companyId}/users`)
  return response.data
}

/**
 * Create a new client portal user.
 */
export async function createClientUser(
  companyId: string,
  data: CreateClientUserData
): Promise<{ success: boolean; data: ClientUser }> {
  const response = await apiClient.post<{ success: boolean; data: ClientUser }>(`/clients/${companyId}/users`, data)
  return response.data
}
